import { Divider } from 'component-library';
import { useTranslation } from 'react-i18next';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { getBenchmarkClass } from '@/components/Charts/LstHistorical';
import { GraphFact } from '@/components/Charts/types';
import { NCCard, NCCard2SectionLayout, NCCardComplianceFooter, NCCardHeader } from '@/components/NCCard/NCCard';
import { LstExplainer } from '@/components/NCExplainers/LstExplainer';
import { COMPLIANCE_LOGOS_LST } from '@/config/constants';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray, getFactsFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { isNullish } from '@/utils/booleans';

import { LstGraphTile } from './GraphTile';
import { LstStatsTile } from './StatsTile';

export const LandSurfaceTemperature = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('shared.ncaDetail.details.landSurfaceTemperature.labels.landSurfaceTemperature')}>
      <LandSurfaceTemperatureCard />
    </PlotCardWrapper>
  );
};

const factNames = {
  latest: IndicatorName.lst,
  oneYearUplift: IndicatorName.lst_previous_1y_uplift,
  oneYearPctChange: IndicatorName.lst_previous_1y_pct_change,
  historicGraph: IndicatorName.lst_historic_graph,
  forecastGraph: IndicatorName.lst_forecast_graph,
  benchmarkGraph: IndicatorName.lst_benchmark_graph,
};

const LandSurfaceTemperatureCard = () => {
  const { t } = useTranslation();

  const facts = usePlotFacts(Object.values(factNames));

  const latestFact = getFactFromFactArray<number>(facts, factNames.latest);
  const oneYearUpliftFact = getFactFromFactArray<number>(facts, factNames.oneYearUplift);
  const oneYearPctChangeFact = getFactFromFactArray<number>(facts, factNames.oneYearPctChange);
  const historicData = getFactFromFactArray<GraphFact[]>(facts, factNames.historicGraph)?.value;
  const forecastData = getFactFromFactArray<GraphFact[]>(facts, factNames.forecastGraph)?.value;
  const benchmarkData =
    getFactsFromFactArray<GraphFact[]>(facts, factNames.benchmarkGraph)?.flatMap((fact) => fact.value ?? []) ?? [];

  const latestBenchmarkPoint = [...benchmarkData]
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .at(-1);

  if (isNullish(latestFact?.value) && !historicData?.length) {
    return null;
  }

  return (
    <NCCard explainer={<LstExplainer />} data-testid='lst-nca'>
      <NCCardHeader
        title={t('shared.ncaDetail.details.landSurfaceTemperature.labels.landSurfaceTemperature')}
        className='lg:grid-cols-[1fr_1fr]'
      />
      <NCCard2SectionLayout>
        <LstGraphTile historicData={historicData} forecastData={forecastData} benchmarkData={benchmarkData} />
        <LstStatsTile
          latestFact={latestFact}
          oneYearUpliftFact={oneYearUpliftFact}
          oneYearPctChangeFact={oneYearPctChangeFact}
          benchmark={latestBenchmarkPoint && getBenchmarkClass(latestBenchmarkPoint)}
        />
      </NCCard2SectionLayout>
      <Divider />
      <NCCardComplianceFooter logos={COMPLIANCE_LOGOS_LST} />
    </NCCard>
  );
};
